import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Ticket, Trophy, ExternalLink, Clock, ShieldCheck, AlertTriangle, Sparkles, Star, Ban } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_STYLE = {
  raffle: { cls: "bg-blaze/10 text-blaze-dark border-blaze/30", Icon: Ticket },
  super_tag: { cls: "bg-forest/10 text-forest border-forest/30", Icon: Star },
  super_hunt: { cls: "bg-forest/10 text-forest border-forest/30", Icon: Star },
  special_permit: { cls: "bg-charcoal/8 text-charcoal border-charcoal/25", Icon: Sparkles },
  premium_hunt: { cls: "bg-[#B7761F]/10 text-[#B7761F] border-[#B7761F]/30", Icon: Trophy },
  once_in_a_lifetime: { cls: "bg-saddle/12 text-saddle border-saddle/30", Icon: Ban },
};

const money = (v) => (v == null ? null : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: v % 1 ? 2 : 0 })}`);
// Slugs arrive lowercase-hyphenated ("new-mexico") — normalize to canonical "New Mexico"
// for BOTH display and API filtering (backend matches jurisdiction exactly).
const titleCase = (s) => (s || "").replace(/[-_]/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

function DeadlineChip({ s }) {
  if (s.deadline_status === "open") {
    const urgent = s.days_until <= 21;
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border ${urgent ? "bg-blaze text-white border-blaze" : "bg-[#2E7D46]/10 text-[#2E7D46] border-[#2E7D46]/30"}`}
        data-testid={`special-deadline-${s.id}`}>
        <Clock size={11} /> {s.deadline_label} {s.next_deadline} · {s.days_until} day{s.days_until === 1 ? "" : "s"} left
      </span>
    );
  }
  if (s.deadline_status === "closed") {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border bg-charcoal/5 text-charcoal/55 border-charcoal/20" data-testid={`special-deadline-${s.id}`}><Clock size={11} /> Closed for this cycle{s.next_deadline ? ` (${s.deadline_label?.toLowerCase()} was ${s.next_deadline})` : ""}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border bg-[#B7761F]/10 text-[#B7761F] border-[#B7761F]/30" data-testid={`special-deadline-${s.id}`}><Clock size={11} /> Dates not yet verified</span>;
}

function SpecialCard({ s, showState }) {
  const st = TYPE_STYLE[s.opportunity_type] || TYPE_STYLE.special_permit;
  const link = s.program_url || s.source_url;
  const speciesChips = s.species_covered?.length ? s.species_covered : (s.species && s.species !== "Multiple" ? [s.species] : null);
  return (
    <div className="bg-white border border-saddle/20 p-4 flex flex-col gap-2.5 hover:border-forest/40 transition-colors" data-testid={`special-card-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border ${st.cls}`}><st.Icon size={10} /> {s.type_label}</span>
          <h3 className="font-serif text-lg text-forest leading-snug mt-1.5">{s.program_name}</h3>
          {showState && <p className="text-[11px] text-charcoal/50">{s.jurisdiction}{s.source_agency ? ` · ${s.source_agency}` : ""}</p>}
        </div>
        {s.confidence === "verified"
          ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#2E7D46] shrink-0" title={`Verified against ${s.source_agency || "the official source"}${s.date_verified ? ` on ${s.date_verified}` : ""}`}><ShieldCheck size={13} /> Verified</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#B7761F] shrink-0" title={s.verification_note || "Some details need review"}><AlertTriangle size={13} /> Needs review</span>}
      </div>

      {speciesChips
        ? <div className="flex flex-wrap gap-1">{speciesChips.map((sp) => <span key={sp} className="bg-bone text-charcoal/70 text-[10px] font-semibold px-2 py-0.5 border border-saddle/15">{sp}</span>)}</div>
        : <p className="text-[11px] text-charcoal/45 italic">Species covered: not yet verified — see official page</p>}

      <div className="text-sm text-charcoal/75 space-y-1">
        {s.ticket_price != null && <p><span className="text-charcoal/45 text-xs uppercase tracking-wide">Entry:</span> <span className="font-semibold tabular-nums">{money(s.ticket_price)}</span>{s.max_tickets ? <span className="text-charcoal/50 text-xs"> · {s.max_tickets} per hunter</span> : ""}</p>}
        {s.ticket_bundle && <p className="text-xs text-charcoal/60">{s.ticket_bundle}</p>}
        {s.special_fee_note && <p className="text-xs text-charcoal/60">{s.special_fee_note}</p>}
        {s.prizes_note && <p className="text-xs text-charcoal/60">{s.prizes_note}</p>}
        {s.oil_note && <p className="text-xs text-charcoal/60">{s.oil_note}</p>}
        {s.application_window && <p className="text-xs text-charcoal/60"><span className="uppercase tracking-wide text-[10px] text-charcoal/45">Window:</span> {s.application_window}</p>}
        {s.eligibility_note && <p className="text-xs text-charcoal/60"><span className="uppercase tracking-wide text-[10px] text-charcoal/45">Eligibility:</span> {s.eligibility_note}</p>}
        {s.resident_eligible === true && s.nonresident_eligible === false && <p className="text-[11px] font-semibold text-saddle">Residents only</p>}
        {s.verification_note && <p className="text-[11px] text-[#B7761F] flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> {s.verification_note}</p>}
      </div>

      <div className="mt-auto pt-1 flex items-center justify-between gap-2 flex-wrap">
        <DeadlineChip s={s} />
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blaze hover:underline" data-testid={`special-link-${s.id}`}>
            Official page <ExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

export function SpecialDrawsPanel({ state, species, title, blurb, limit }) {
  const [data, setData] = useState(null);
  const apiState = state ? titleCase(state) : undefined;
  useEffect(() => {
    let cancelled = false;  // ignore stale responses when the filter changes mid-flight
    setData(null);
    api.specialDraws({ state: apiState, species })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ special_draws: [] }); });
    return () => { cancelled = true; };
  }, [apiState, species]);

  const all = data?.special_draws || [];
  const rows = limit ? all.slice(0, limit) : all;

  return (
    <section data-testid="special-draws-panel" id="special-draws">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-forest">
            <Ticket size={18} className="text-blaze" />
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">{title || (state ? `${titleCase(state)} special draws & raffles` : "Special draws & raffles")}</h2>
          </div>
          <p className="text-sm text-charcoal/55 mt-1 max-w-2xl">{blurb || "Raffles, Super Tags, once-in-a-lifetime rules and premium tags — legitimate extra ways at a tag, all verified against official agency sources."}</p>
        </div>
        {state && <Link to={`/planner?state=${encodeURIComponent(titleCase(state))}&special=1`} className="text-sm font-semibold text-blaze hover:underline" data-testid="special-open-planner">Open in Planner</Link>}
      </div>

      {data === null ? (
        <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : rows.length === 0 ? (
        <div className="mt-4 border border-dashed border-saddle/30 p-5 text-center" data-testid="special-empty">
          <p className="font-serif text-lg text-forest">No verified special draws on file{apiState ? ` for ${apiState}` : ""} yet</p>
          <p className="text-charcoal/55 text-sm mt-1">HomeRange only lists programs confirmed against official sources — coverage grows state by state.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4" data-testid="special-grid">
          {rows.map((s) => <SpecialCard key={s.id} s={s} showState={!state} />)}
        </div>
      )}
      <p className="text-[11px] text-charcoal/45 mt-3 flex items-center gap-1.5">
        <ShieldCheck size={12} className="text-[#2E7D46]" /> Program details, dates and costs come from official agency pages. Anything not yet verified is marked — never estimated.
      </p>
    </section>
  );
}
